import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
  ReferenceArea,
  Rectangle,
  Tooltip,
} from 'recharts';
import { useTheme } from 'styled-components';
import CustomLabel from './CustomLabel/CustomLabel';
import { TooltipCont } from './PRMBarChart.styles';
import XAxisLabel from './XAxisLabel/XAxisLabel';
import { ECode, EGroup, IRange } from '../../services/reports';

interface IPayload {
  payload: {
    code: ECode;
    surveys: { name: string }[];
  };
}

interface ISurvey {
  share: number;
  name: string;
  numResponses: number;
}

interface IData {
  code: ECode;
  group: EGroup;
  surveys: ISurvey[];
}

type TLabelListData = Pick<IData, 'surveys'>;

interface IPRMBarChart {
  data: IData[];
  reference: Record<ECode, IRange>;
}

const PRMBarChart: FC<IPRMBarChart> = ({ data, reference }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [chartParams, setChartParams] = useState({
    bar: false,
    area: false,
    barIndex: 0,
  });

  const colors: Record<ECode, string[]> = {
    [ECode.S1]: [
      theme.colors.accent.green[100],
      theme.colors.accent.green[70],
      theme.colors.accent.green[30],
    ],
    [ECode.S2]: [
      theme.colors.accent.blue[100],
      theme.colors.accent.blue[70],
      theme.colors.accent.blue[30],
    ],
    [ECode.S3]: [
      theme.colors.accent.orange[100],
      theme.colors.accent.orange[70],
      theme.colors.accent.orange[30],
    ],
    [ECode.S4]: [
      theme.colors.accent.violet[100],
      theme.colors.accent.violet[70],
      theme.colors.accent.violet[30],
    ],
    [ECode.S5]: [
      theme.colors.accent.red[100],
      theme.colors.accent.red[70],
      theme.colors.accent.red[30],
    ],
  };

  const count = Math.max(...data.map(item => item.surveys.length));

  return (
    <BarChart width={1080} height={620} margin={{ top: 30, bottom: 20 }} {...{ data }}>
      <CartesianGrid stroke={theme.colors.neutral.primary[20]} vertical={false} syncWithTicks />
      <XAxis
        axisLine={false}
        tick={props => <XAxisLabel {...props} data={data} width={1080} colors={colors} />}
        tickLine={false}
        xAxisId="0"
        orientation="top"
      />
      <XAxis
        axisLine={false}
        dataKey="group"
        fill={theme.colors.neutral.primary[60]}
        fontSize={14}
        fontWeight={700}
        orientation="bottom"
        tickLine={false}
        xAxisId="1"
      />
      <YAxis
        axisLine={false}
        domain={[0, 60]}
        fontSize={14}
        fontWeight={400}
        padding={{ top: 12, bottom: 12 }}
        tickFormatter={value => (value % 10 === 0 ? `${value} %` : '')}
        tickLine={false}
        tickCount={13}
        tickSize={24}
      />
      <Tooltip
        content={({ payload }) => {
          const code = (payload as IPayload[])[0]?.payload.code;
          const name =
            chartParams.barIndex !== -1
              ? (payload as IPayload[])[0]?.payload.surveys[chartParams.barIndex]?.name
              : '';

          const { min, max } = reference[code] ?? {};
          if (chartParams.area === true) {
            return <TooltipCont>{t('report.prm.chart.tooltip', { min, max })}</TooltipCont>;
          }
          if (chartParams.bar === true) {
            return <TooltipCont>{name}</TooltipCont>;
          }
        }}
        active={chartParams.area || chartParams.bar}
        cursor={false}
        isAnimationActive={false}
      />
      {data.map(({ code, group }) => (
        <ReferenceArea
          fill={`${colors[code][2]}80`}
          key={group}
          radius={4}
          shape={(props: { x: number; width: number }) => (
            <Rectangle {...props} x={props.x + 20} width={props.width - 40} />
          )}
          stroke={colors[code][2]}
          xAxisId={1}
          yAxisId={0}
          x1={group}
          y1={reference[code].min}
          x2={group}
          y2={reference[code].max}
          onMouseEnter={() =>
            setChartParams(prevState => ({
              ...prevState,
              area: true,
            }))
          }
          onMouseLeave={() =>
            setChartParams(prevState => ({
              ...prevState,
              area: false,
            }))
          }
        />
      ))}
      {Array(count)
        .fill(1)
        .map((value, index, array) => (
          <Bar
            barSize={array.length > 1 ? 32 : 48}
            dataKey={({ surveys }) => surveys[array.length - index - 1]?.share}
            key={value + index}
            radius={4}
            isAnimationActive={false}
            onMouseEnter={() =>
              setChartParams(prevState => ({
                ...prevState,
                bar: true,
                area: false,
                barIndex: array.length - index - 1,
              }))
            }
            onMouseLeave={() =>
              setChartParams(prevState => ({
                ...prevState,
                bar: false,
                area: false,
                barIndex: array.length - index - 1,
              }))
            }
          >
            {data.map(({ code }, idx) => {
              return <Cell key={`cell-${idx}`} fill={colors[code][array.length - index - 1]} />;
            })}
            <LabelList
              dataKey={data => {
                const surveys = (data as TLabelListData)?.surveys;

                const currentSurvey = surveys[array.length - index - 1];
                const numResponses = currentSurvey?.numResponses ?? 0;

                return `${currentSurvey.share}%\n(${numResponses})`;
              }}
              content={props => <CustomLabel {...props} />}
              fill={theme.colors.neutral.primary[0]}
              fontSize={12}
              fontWeight={700}
              offset={8}
              position="insideTop"
            />
          </Bar>
        ))}
    </BarChart>
  );
};

export default PRMBarChart;
